import React from 'react';

const formatIDR = (val) => {
  if (!val) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(val);
};

const ReviewRow = ({ label, value }) => (
  <div className='d-flex justify-content-between border-bottom pb-2 mb-2'>
    <span className='text-muted small'>{label}</span>
    <span className='fw-semibold small text-end ms-3'>{value || '-'}</span>
  </div>
);

const VECIReview = ({ watch, questions = { op: [], ad: [], mp: [], s: [] } }) => {
  const data = watch();
  const respondent = data.respondent || {};
  const businessInfo = data.businessInfo || {};
  const competitors = data.competitors || [];
  const answers = data.answers || {};

  const allSections = [
    { title: 'Operasional (OP)', data: questions.op },
    { title: 'Adaptabilitas (AD)', data: questions.ad },
    { title: 'Kinerja Pasar (MP)', data: questions.mp },
    { title: 'Konteks Strategis (S)', data: questions.s },
    { title: 'Keterhubungan (E)', data: questions.e },
  ];

  return (
    <div className='animate-slide-up vstack gap-4'>
      {/* Banner */}
      <div className='card border-0 shadow-sm rounded-4 p-4 p-md-5 bg-primary text-white position-relative overflow-hidden'>
        <div className='position-relative z-1'>
          <h4 className='fw-bold mb-2'>
            <i className='bi bi-check2-circle me-2'></i>Review & Submit
          </h4>
          <p className='opacity-75 mb-0'>
            Seluruh data telah terkumpul. Silakan tinjau ringkasan di bawah
            sebelum menekan tombol <strong>Submit Survey</strong>.
          </p>
        </div>
        <i
          className='bi bi-check2-all position-absolute bottom-0 end-0 display-1 opacity-25'
          style={{
            transform: 'rotate(-15deg)',
            marginBottom: '-20px',
            marginRight: '-10px',
          }}
        ></i>
      </div>

      <div className='row g-4'>
        {/* Profil Direktur BUMDesa */}
        <div className='col-md-6'>
          <div className='card border-0 shadow-sm rounded-4 h-100'>
            <div className='card-header bg-white border-0 pt-4 px-4 pb-2 d-flex align-items-center'>
              <div className='bg-primary bg-opacity-10 p-2 rounded-3 me-3'>
                <i className='bi bi-person-badge text-primary'></i>
              </div>
              <h6 className='fw-bold mb-0'>Profil Direktur BUMDesa</h6>
            </div>
            <div className='card-body px-4 pb-4 pt-2'>
              <ReviewRow label='Nama' value={respondent.respondentName} />
              <ReviewRow
                label='Jenis Kelamin'
                value={respondent.gender === 'Male' ? 'Laki-laki' : 'Perempuan'}
              />
              <ReviewRow
                label='Umur'
                value={respondent.age ? `${respondent.age} tahun` : '-'}
              />
              <ReviewRow
                label='Pendidikan Terakhir'
                value={respondent.lastEducation}
              />
              <ReviewRow
                label='Jabatan di BUMDes'
                value={respondent.positionAtBumdes}
              />
              <ReviewRow label='No. WhatsApp' value={respondent.phoneNumber} />
              <ReviewRow
                label='Alamat Domisili'
                value={respondent.residentialAddress}
              />
            </div>
          </div>
        </div>

        {/* Info Bisnis */}
        <div className='col-md-6'>
          <div className='card border-0 shadow-sm rounded-4 h-100'>
            <div className='card-header bg-white border-0 pt-4 px-4 pb-2 d-flex align-items-center'>
              <div className='bg-primary bg-opacity-10 p-2 rounded-3 me-3'>
                <i className='bi bi-building text-primary'></i>
              </div>
              <h6 className='fw-bold mb-0'>Informasi Bisnis BUMDes</h6>
            </div>
            <div className='card-body px-4 pb-4 pt-2'>
              <ReviewRow label='Sektor Utama' value={businessInfo.mainSector} />
              <ReviewRow
                label='Modal Awal'
                value={formatIDR(businessInfo.initialCapital)}
              />
              <ReviewRow
                label='Modal Saat Ini'
                value={formatIDR(businessInfo.currentCapital)}
              />
              <ReviewRow
                label='Pendapatan Kotor Rata-rata per Bulan'
                value={formatIDR(businessInfo.monthlyGrossIncome)}
              />
              <ReviewRow
                label='Pendapatan Kotor dalam Satu Tahun Terakhir'
                value={formatIDR(businessInfo.annualGrossIncome)}
              />
              <div className='mt-2'>
                <span className='text-muted small d-block mb-1'>
                  Produk / Jasa Utama
                </span>
                <div className='d-flex flex-wrap gap-1'>
                  {businessInfo.productsServices
                    ?.filter((p) => p)
                    .map((p, i) => (
                      <span
                        key={i}
                        className='badge bg-light text-dark fw-normal border rounded-pill px-3'
                      >
                        {p}
                      </span>
                    ))}
                </div>
              </div>
              {businessInfo.secondarySector && (
                <ReviewRow
                  label='Sektor Bisnis Lainnya'
                  value={businessInfo.secondarySector}
                />
              )}
              {businessInfo.secondaryProductsServices?.filter((p) => p)
                .length > 0 && (
                <div className='mt-2'>
                  <span className='text-muted small d-block mb-1'>
                    Produk / Jasa Lainnya
                  </span>
                  <div className='d-flex flex-wrap gap-1'>
                    {businessInfo.secondaryProductsServices
                      .filter((p) => p)
                      .map((p, i) => (
                        <span
                          key={i}
                          className='badge bg-light text-dark fw-normal border rounded-pill px-3'
                        >
                          {p}
                        </span>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Daftar Pesaing */}
        <div className='col-12'>
          <div className='card border-0 shadow-sm rounded-4'>
            <div className='card-header bg-white border-0 pt-4 px-4 pb-2 d-flex align-items-center'>
              <div className='bg-primary bg-opacity-10 p-2 rounded-3 me-3'>
                <i className='bi bi-people text-primary'></i>
              </div>
              <h6 className='fw-bold mb-0'>
                Daftar Pesaing ({competitors.length})
              </h6>
            </div>
            <div className='card-body px-4 pb-4 pt-2'>
              <div className='table-responsive'>
                <table className='table table-sm table-hover align-middle mb-0'>
                  <thead className='small text-muted text-uppercase'>
                    <tr>
                      <th className='py-2 border-0 bg-light rounded-start ps-3'>
                        #
                      </th>
                      <th className='py-2 border-0 bg-light'>Nama Usaha</th>
                      <th className='py-2 border-0 bg-light'>Lokasi</th>
                      <th className='py-2 border-0 bg-light text-center rounded-end'>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitors.map((comp, i) => (
                      <tr key={i}>
                        <td className='ps-3 text-muted small'>{i + 1}</td>
                        <td className='fw-bold'>{comp.name || 'N/A'}</td>
                        <td className='text-muted small'>
                          {[
                            comp.village,
                            comp.subDistrict,
                            comp.regency,
                            comp.province,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </td>
                        <td className='text-center'>
                          {comp.isStrongest ? (
                            <span className='badge bg-light bg-opacity-15 text-primary rounded-pill px-3 fw-semibold'>
                              <i className='bi bi-trophy-fill me-1'></i>Terkuat
                            </span>
                          ) : (
                            <span className='badge bg-light text-muted rounded-pill px-3'>
                              Pesaing Biasa
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* VECI INSTRUMENTS SUMMARY */}
        <div className='col-12 mt-4'>
          <h5 className='fw-bold mb-3'>
            <i className='bi bi-card-checklist me-2 text-primary'></i> Ringkasan
            Instrumen VECI
          </h5>
          <div className='vstack gap-3'>
            {allSections.map((sec, idx) => (
              <div
                key={idx}
                className='card border-0 shadow-sm rounded-4 overflow-hidden'
              >
                <div className='card-header bg-light bg-opacity-50 border-0 px-4 py-3'>
                  <h6 className='fw-bold mb-0 text-primary'>{sec.title}</h6>
                </div>
                <div className='card-body p-0'>
                  <div className='table-responsive'>
                    <table className='table table-hover align-middle mb-0'>
                      <thead className='table-light'>
                        <tr>
                          <th className='ps-4' style={{ width: '80px' }}>
                            Kode
                          </th>
                          <th>Pertanyaan</th>
                          <th className='text-center' style={{ width: '100px' }}>
                            Skor/Pilihan
                          </th>
                          <th>Catatan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sec.data.map((q) => {
                          const ans = answers[q.questionCode] || {};
                          const isContext = q.type !== 'scale';
                          const scoreOrVal = isContext ? ans.optionValue : ans.score;

                          return (
                            <tr key={q.questionCode}>
                              <td className='ps-4 fw-bold text-muted'>
                                {q.questionCode}
                              </td>
                              <td
                                className='small text-wrap'
                                style={{ maxWidth: '300px' }}
                              >
                                {q.title}
                              </td>
                              <td className='text-center'>
                                {scoreOrVal ? (
                                  isContext && q.type !== 'choice' && q.type !== 'multiple_choice' ? (
                                    <div className="small text-wrap text-start fst-italic text-muted border-start border-3 border-info ps-2 py-1" style={{ maxWidth: '200px' }}>
                                      {scoreOrVal}
                                    </div>
                                  ) : (
                                    <span className={`badge rounded-pill px-3 py-2 ${isContext ? 'bg-info bg-opacity-10 text-info' : 'bg-primary'}`}>
                                      {isContext ? String(scoreOrVal).toUpperCase() : scoreOrVal}
                                    </span>
                                  )
                                ) : (
                                  <span className='text-danger small'>
                                    Belum diisi
                                  </span>
                                )}
                              </td>
                              <td className='small text-muted'>
                                {ans.note || '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VECIReview;
